import React, { useState } from "react";
import { FaMale, FaFemale } from "react-icons/fa";
import { Pie } from "react-chartjs-2";
import {
	Chart as ChartJS,
	ArcElement,
	Tooltip,
	Legend
} from "chart.js";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

ChartJS.register(ArcElement, Tooltip, Legend);

// Default state-level data
const DEFAULT_DATA = {
	male: 600038,
	female: 545898,
};

// Generate previous period dummy values (92% of current)
function generatePrevious(data) {
	return {
		male: Math.round(data.male * 0.92),
		female: Math.round(data.female * 0.92),
	};
}

export default function FullyImmunizedCard({ selectedDistrict = null, seed = null }) {
	const [open, setOpen] = useState(false);

	// Current period data
	const current = selectedDistrict
		? {
			male: Math.round(DEFAULT_DATA.male * 0.2),
			female: Math.round(DEFAULT_DATA.female * 0.2),
		}
		: DEFAULT_DATA;

	// Previous period data
	const previous = generatePrevious(current);

	const totalCurrent = current.male + current.female;
	const totalPrevious = previous.male + previous.female;

	const diff = totalCurrent - totalPrevious;
	const pct = (diff / totalPrevious) * 100;
	const isIncrease = diff >= 0;

	const pieData = {
		labels: ["Male", "Female"],
		datasets: [
			{
				data: [current.male, current.female],
				backgroundColor: ["#3b82f6", "#ec4899"],
				hoverBackgroundColor: ["#2563eb", "#db2777"],
			},
		],
	};

	return (
		<>
			{/* Small card */}
			<button
				onClick={() => setOpen(true)}
				className="inline-flex items-center gap-3 px-3 py-2 rounded-lg shadow-sm bg-white border hover:shadow-md w-full"
			>
				<div className="w-10 h-10 flex items-center justify-center rounded-md bg-green-100">
					<FaMale className="text-green-600 w-5 h-5" />
				</div>

				<div className="text-left">
					<div className="text-xs text-slate-600 font-medium">Fully Immunized</div>
					<div className="text-lg font-semibold">{totalCurrent.toLocaleString()}</div>
					<div className="text-[11px]">
						<span className={isIncrease ? "text-green-600" : "text-red-600"}>
							{isIncrease ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
						</span>
						<span className="text-slate-500"> vs last month</span>
					</div>

					{/* <div className="flex items-center gap-2">
						

						<div
							className={`flex items-center text-xs font-semibold ${isIncrease ? "text-green-600" : "text-red-600"
								}`}
						>
							{isIncrease ? (
								<FaArrowUp className="w-3 h-3" />
							) : (
								<FaArrowDown className="w-3 h-3" />
							)}
							{Math.abs(pct).toFixed(1)}%
						</div>
					</div>

					<div className="text-[10px] text-slate-400">vs previous period</div> */}
				</div>
			</button>

			{/* Offcanvas panel */}
			{open && (
				<div className="fixed inset-0 z-50 flex">
					<div
						className="absolute inset-0 bg-black/40"
						onClick={() => setOpen(false)}
						aria-hidden
					/>

					<aside className="relative ml-auto w-full max-w-md h-full bg-white shadow-2xl overflow-auto">
						<div className="p-4 border-b flex items-center justify-between">
							<h3 className="text-lg font-semibold">
								Fully Immunized Children (9–12 months)
							</h3>
							<button
								onClick={() => setOpen(false)}
								className="px-3 py-1 rounded bg-slate-100 text-sm"
							>
								Close
							</button>
						</div>

						<div className="p-4 space-y-6">
							<div className="w-full h-50">
								<Pie data={pieData} />
							</div>

							{/* Male */}
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 flex items-center justify-center rounded-md bg-blue-100">
									<FaMale className="text-blue-600 w-5 h-5" />
								</div>
								<div className="flex-1">
									<div className="text-sm font-medium">Male</div>
									<div className="flex items-center gap-2">
										<div className="text-lg font-semibold">{current.male.toLocaleString()}</div>

										{(() => {
											const diff = current.male - previous.male;
											const pct = (diff / previous.male) * 100;
											const inc = diff >= 0;
											return (
												<span
													className={`flex items-center text-xs font-semibold ${inc ? "text-green-600" : "text-red-600"
														}`}
												>
													{inc ? (
														<FaArrowUp className="w-3 h-3" />
													) : (
														<FaArrowDown className="w-3 h-3" />
													)}
													{Math.abs(pct).toFixed(1)}%
												</span>
											);
										})()}
									</div>
								</div>
							</div>

							{/* Female */}
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 flex items-center justify-center rounded-md bg-pink-100">
									<FaFemale className="text-pink-600 w-5 h-5" />
								</div>
								<div className="flex-1">
									<div className="text-sm font-medium">Female</div>
									<div className="flex items-center gap-2">
										<div className="text-lg font-semibold">{current.female.toLocaleString()}</div>

										{(() => {
											const diff = current.female - previous.female;
											const pct = (diff / previous.female) * 100;
											const inc = diff >= 0;
											return (
												<span
													className={`flex items-center text-xs font-semibold ${inc ? "text-green-600" : "text-red-600"
														}`}
												>
													{inc ? (
														<FaArrowUp className="w-3 h-3" />
													) : (
														<FaArrowDown className="w-3 h-3" />
													)}
													{Math.abs(pct).toFixed(1)}%
												</span>
											);
										})()}
									</div>
								</div>
							</div>

							<div className="mt-2 text-sm text-slate-500">
								Source: HMIS-Apr24 to Mar25
							</div>
						</div>
					</aside>
				</div>
			)}
		</>
	);
}
